import { Box, CircularProgress, Tab, Tabs } from "@mui/material";
import BaseWrapper from "../../../components/BaseWrapper/BaseWrapper";
import { API } from "../../../lib/api";
import { useEffect, useState } from "react";
import { Assessment, FolderSpecial, Leaderboard, Storm } from "@mui/icons-material";
import { FormProvider } from "../../../contexts/CreateRecordFormContext";
import RecordModal from "../../../components/RecordModal/RecordModal";
import Feedback from "../../../components/Feedback/Feedback";
import { useAuth } from "../../../contexts/AuthContext";
import ForecastCharts from "./ForecastCharts";
import SavedForecasts from "./SavedForecasts";
import CloseoutModal from "./CloseoutModal";
import ForecastGrid from "./ForecastGrid";
import ReportingTool from "./ReportingTool";

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const RevenueLinesFlat = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [lines, setLines] = useState([])
    const [activeLines, setActiveLines] = useState([])
    const [selectedRows, setSelectedRows] = useState([]);
    const [modalOpen, setModalOpen] = useState(false)
    const [feedback,setFeedback] = useState({})
    const [warning, setWarning] = useState(false)
    const [years, setYears] = useState([])
    const [value, setValue] = useState(0);
    const [closeoutOpen, setCloseoutOpen] = useState(false)
    const role = String(user?.role || localStorage.getItem("role") || "").trim().toLowerCase();
    const isAdmin = role === "admin";
    const sessionUserId = user?.userId || localStorage.getItem("id");

    const handleChange = (event, newValue) => setValue(newValue)
    const handleOpenModal = () => selectedRows.length ? setModalOpen(true) : setFeedback({open: true, message: "Select at least one record to update!", type: "info"})
    const handleCloseModal = () => setModalOpen(false);
    const handleCloseCloseoutModal = () => setCloseoutOpen(false)
    const handleCloseout = () => setCloseoutOpen(true)

    const handleSelectionChange = (selection) => {
        setSelectedRows(selection)
        selection.length > 1 ? setWarning(true) : setWarning(false)
    }

    const mergeUpdatedRevenueLines = (previousLines, updatedRevenueLines) => {
        const updatedById = new Map(
            (updatedRevenueLines || []).map((line) => [String(line?._id), line])
        );

        return previousLines.map((line) => {
            const currentLineId = String(line?.lineId || line?._id || "");
            const updatedLine = updatedById.get(currentLineId);

            if (!updatedLine) {
                return line;
            }

            return {
                ...line,
                ...updatedLine,
                lineId: line.lineId || updatedLine._id,
            };
        });
    };

    const handleSubmit = async (updateData) => {
        let apiFormattedData = []
        let updatedFields = {}

        Object.keys(updateData).forEach(key => {
            if (updateData[key] !== "" && updateData[key] != null) {
                Object.assign(updatedFields, { [key]: updateData[key] } )
            }
        });

        if (!Object.keys(updatedFields).length) {
            setFeedback({ open: true, message: "Choose at least one field to update.", type: "info" })
            return
        }

        for (let i=0; i<selectedRows.length; i++) {
            apiFormattedData.push({_id: selectedRows[i], data: updatedFields })
        }

        const response = await API.Lines.UpdateForecast(apiFormattedData)
        if (response.status === 200) {
            const updatedRevLines = response.data.data
            setLines(prevLines => mergeUpdatedRevenueLines(prevLines, updatedRevLines))
            setActiveLines(prevLines => mergeUpdatedRevenueLines(prevLines, updatedRevLines))
            setFeedback(prevData => ({...prevData, open: true, message: response.data.message, type: "success" }))
        } else {
            setFeedback(prevData => ({ ...prevData, open: true, message: response?.data?.message || 'Error updating rev lines...', type: "error" }))
        }
        setSelectedRows([])
        handleCloseModal()
    }

    const handleCloseoutSubmit = async (form) => {
        const validDataSet = activeLines.length > 0

        const formatLines = activeLines.map(line => ({
            ...line,
            owner: line.owner?._id || null
        }))

        const formatRequest = { title: form.title, createdBy: localStorage.id, validDataSet: validDataSet, lines: formatLines }

        const response = await API.Forecasting.Create(formatRequest)

        response.status === 200 ? setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "success" })) : setFeedback(prevData => ({ ...prevData, open: true, message: 'Closeout Failed...', type: "error" }))

        setCloseoutOpen(false)
    }

    const updateFields = [
        { name: "amount", label: "Amount", type: "number"},
        ...(isAdmin ? [
            { name: "marginPercentage", label: "Margin %", type: "number" },
            { name: "marginAmount", label: "Margin Amount", type: "number" },
        ] : []),
        { name: "bookedOrdersStage", label: "Booked Orders Stage", type: "autocomplete", options: ['01 - Assign Resources', '02 - Submittals Sent', '03 - Submittals Approved', '04 - Shipped', '05 - Installed', '06 - Commissioned', '07 - Revenue Taken', '10 - Closed']},
        { name: "shipDate", label: "Ship Date", type: "date" },
    ]

    useEffect(() => {
        async function FetchData() {
            const response = await API.Reporting.RevenueLinesFlat()

            if (response.status === 200) {
                const currentYear = new Date().getFullYear()
                const filteredLines = response.data.filter(item => new Date(item.shipDate).getFullYear() === currentYear)

                setLines(response.data)
                setActiveLines(filteredLines)

                // -- Set Years array -- //
                const yearsFormatted = [
                    ...new Set(
                        response.data
                            .map(item => new Date(item.shipDate).getFullYear())
                            .filter(year => !isNaN(year))
                    ),
                ].sort((a, b) => a - b);
                setYears(yearsFormatted)
            }

            setLoading(false)
        }
        FetchData()
    }, [])

    return (
        <BaseWrapper content={
            <Box>
                {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            ) : (
                <FormProvider>
                    {/* Modals & Feedback */}
                    <RecordModal open={modalOpen} handleClose={handleCloseModal} fields={updateFields} title={warning ? "WARNING: You Are Updating Multiple Revenue Lines!" : "Update Revenue Line(s)"} onSubmit={handleSubmit} warning={warning} />
                    <Feedback props={feedback} setFeedback={setFeedback} />
                    <CloseoutModal open={closeoutOpen} handleClose={handleCloseCloseoutModal} title={"Closeout Forecast"} onSubmit={handleCloseoutSubmit} />

                    {/* Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                            <Tab icon={<Storm />} label="Forecast Grid" {...a11yProps(0)} />
                            <Tab icon={<Leaderboard />} label="Charts/Graphs" {...a11yProps(1)} />
                            <Tab icon={<FolderSpecial />} label="Saved Forecasts" {...a11yProps(2)} />
                            <Tab icon={<Assessment />} label="Reporting Tool" {...a11yProps(3)} />
                        </Tabs>
                    </Box>

                    {/* Forecast Grid */}
                    <CustomTabPanel value={value} index={0}>
                        <ForecastGrid lines={lines} activeLines={activeLines} setActiveLines={setActiveLines} years={years} handleOpenModal={handleOpenModal} handleCloseout={handleCloseout} selectedRows={selectedRows} handleSelectionChange={handleSelectionChange} sessionUserId={sessionUserId} />
                    </CustomTabPanel>

                    {/* Charts/Graphs */}
                    <CustomTabPanel value={value} index={1}>
                        <ForecastCharts allLines={lines} />
                    </CustomTabPanel>

                    {/* Saved Forecasts */}
                    <CustomTabPanel value={value} index={2}>
                        <SavedForecasts/>
                    </CustomTabPanel>

                    <CustomTabPanel value={value} index={3}>
                        <ReportingTool liveLines={lines} />
                    </CustomTabPanel>

                </FormProvider>
            )}
            </Box>
        }/>
    )
}

export default RevenueLinesFlat
